"use client"

import { useState, useEffect } from 'react'

type TranslationValue = string | string[] | TranslationObject | TranslationObject[]
interface TranslationObject {
  [key: string]: TranslationValue
}

type TranslationArrayItem = string | Record<string, unknown>

type TranslationData = TranslationObject

function isPlainObject(value: unknown): value is TranslationObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function deepMergeTranslations(base: TranslationData, override: TranslationData): TranslationData {
  const merged: TranslationData = { ...base }

  for (const [key, value] of Object.entries(override)) {
    const existing = merged[key]
    if (isPlainObject(existing) && isPlainObject(value)) {
      merged[key] = deepMergeTranslations(existing, value)
    } else {
      merged[key] = value
    }
  }

  return merged
}

async function loadLocaleFile(locale: string): Promise<TranslationData> {
  const response = await fetch(`/locales/${locale}.json`)
  if (!response.ok) {
    throw new Error(`Failed to load translations for locale: ${locale}`)
  }
  return response.json()
}

type UseTranslationOptions = {
  fallbackLocale?: string
}

/**
 * Translation hook for i18n
 * @param locale - The language locale to use (e.g., 'fa', 'en')
 * @returns t function for accessing translations, and locale info
 */
export function useTranslation(locale: string = 'en', options: UseTranslationOptions = {}) {
  const { fallbackLocale } = options
  const [translations, setTranslations] = useState<TranslationData>({})
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    let isMounted = true
    setIsLoaded(false)

    const loadTranslations = async () => {
      try {
        const primary = await loadLocaleFile(locale)
        let merged = primary

        if (fallbackLocale && fallbackLocale !== locale) {
          try {
            const fallback = await loadLocaleFile(fallbackLocale)
            merged = deepMergeTranslations(fallback, primary)
          } catch (fallbackError) {
            console.error('[useTranslation] Error loading fallback translations:', fallbackError)
          }
        }

        if (isMounted) {
          setTranslations(merged)
          setIsLoaded(true)
        }
      } catch (err) {
        console.error('[useTranslation] Error loading translations:', err)
        if (isMounted) {
          setIsLoaded(true)
        }
      }
    }

    void loadTranslations()

    return () => {
      isMounted = false
    }
  }, [locale, fallbackLocale])

  /**
   * Get translation by key path with optional interpolation
   * @param key - Dot-separated key path (e.g., 'nav.brand', 'hero.title')
   * @param paramsOrFallback - Either an object for interpolation (e.g., { year: 2024 }) or a fallback string
   * @returns Translated string or fallback/key if not found
   * @example t('footer.copyright', { year: 2024 }) => "© 2024 آتیرام..."
   * @example t('nav.brand', 'Fallback') => "آتیرام"
   */
  const t = (key: string, paramsOrFallback?: Record<string, string | number> | string): string => {
    const keys = key.split('.')
    let value: TranslationValue = translations
    
    for (const k of keys) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        value = (value as TranslationObject)[k]
      } else {
        // If paramsOrFallback is a string, use it as fallback
        if (typeof paramsOrFallback === 'string') {
          return paramsOrFallback
        }
        return key
      }
    }
    
    if (typeof value === 'string') {
      // Apply interpolation if params are provided
      if (paramsOrFallback && typeof paramsOrFallback === 'object') {
        return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, paramKey) => {
          return paramsOrFallback[paramKey] !== undefined 
            ? String(paramsOrFallback[paramKey]) 
            : match
        })
      }
      return value
    }
    
    // If paramsOrFallback is a string, use it as fallback
    if (typeof paramsOrFallback === 'string') {
      return paramsOrFallback
    }
    return key
  }

  /**
   * Get translation array by key path
   * @param key - Dot-separated key path (e.g., 'featuresGrid', 'howItWorks.steps')
   * @returns Array of translation objects with string values, array of strings, or empty array
   */
  const tArray = <T extends TranslationArrayItem = TranslationArrayItem>(key: string): T[] => {
    const keys = key.split('.')
    let value: TranslationValue = translations
    
    for (const k of keys) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        value = (value as TranslationObject)[k]
      } else {
        return []
      }
    }
    
    if (Array.isArray(value)) {
      return value as unknown as T[]
    }
    
    return []
  }

  /**
   * Check if current locale is RTL
   */
  const isRTL = locale === 'fa' || locale === 'ar' || locale === 'he'

  /**
   * Get locale for number/date formatting
   */
  const numberLocale = locale === 'fa' ? 'fa-IR' : locale

  return { 
    t, 
    tArray,
    locale, 
    isRTL,
    numberLocale,
    isLoaded 
  }
}